import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Flame, Trophy, Users, Zap } from "lucide-react";
import { powerLeaderboard } from "@/lib/powerApi";

const SCOPES = [
  { id: "xp",         label: "Top members",  icon: <Zap className="w-4 h-4" fill="currentColor" /> },
  { id: "referrals",  label: "Top referrers", icon: <Users className="w-4 h-4" /> },
];

export default function PowerLeaderboard() {
  const [scope, setScope] = useState("xp");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    powerLeaderboard(scope)
      .then((r) => setRows(r.leaderboard || []))
      .catch(() => toast.error("Couldn't load leaderboard"))
      .finally(() => setLoading(false));
  }, [scope]);

  return (
    <div data-testid="power-leaderboard" className="pb-6 space-y-4">
      <div className="rounded-3xl bg-gradient-to-br from-[#F59E0B] to-[#FBBF24] text-[#0A1A2F] p-6 sm:p-7 shadow-xl">
        <div className="inline-flex items-center gap-1.5 bg-black/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3"><Trophy className="w-3 h-3" /> Leaderboard</div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Top of the movement.</h1>
        <p className="text-[#0A1A2F]/70 mt-1 text-sm">Every action counts. Climb the ranks, earn badges.</p>
      </div>

      <div className="flex gap-2">
        {SCOPES.map((s) => (
          <button key={s.id} data-testid={`lb-scope-${s.id}`} onClick={() => setScope(s.id)}
            className={"inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors " + (scope === s.id ? "bg-[#0A1A2F] text-white" : "bg-white border border-[#E4EAF3] text-[#0A1A2F]/70 hover:border-[#0A1A2F]")}>
            {s.icon}{s.label}
          </button>
        ))}
      </div>

      {loading ? <div className="py-16 text-center text-[#0A1A2F]/50">Loading…</div> :
        rows.length === 0 ? (
          <div data-testid="lb-empty" className="bg-white rounded-2xl border border-[#E4EAF3] p-10 text-center text-[#0A1A2F]/60 text-sm">
            No one has climbed the ladder yet. Be first — sign, invite friends, and complete achievements.
          </div>
        ) : (
        <ol data-testid="lb-list" className="bg-white rounded-2xl border border-[#E4EAF3] divide-y divide-[#E4EAF3] shadow-sm overflow-hidden">
          {rows.map((r, i) => (
            <li key={r.user_id} data-testid={`lb-row-${i}`} className="flex items-center gap-3 p-3 hover:bg-[#F6F8FC]">
              <div className={"w-8 h-8 rounded-full flex items-center justify-center font-black text-sm " + (i === 0 ? "bg-[#F59E0B] text-white" : i === 1 ? "bg-[#9CA3AF] text-white" : i === 2 ? "bg-[#B45309] text-white" : "bg-[#F6F8FC] text-[#0A1A2F]")}>{i + 1}</div>
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-white" style={{ background: r.avatar_color }}>{(r.name || "?")[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#0A1A2F] truncate inline-flex items-center gap-1">
                  {r.name}
                  {r.verified && <BadgeCheck className="w-3.5 h-3.5 text-[#1E88FF]" fill="#1E88FF" stroke="white" />}
                </div>
                <div className="text-[11px] text-[#0A1A2F]/50 inline-flex items-center gap-2">
                  <span>Lvl {r.level}</span>
                  <span className="inline-flex items-center gap-0.5"><Flame className="w-3 h-3 text-[#F59E0B]" />{r.streak_days}d</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-black text-[#0A1A2F]">{r.value.toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A1A2F]/50 font-bold">{r.value_label}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
